import { useQuery, UseQueryResult } from '@tanstack/react-query'
import {
  isResourceOfType,
  OpalApiComponent,
  OpalApiResponseDocument,
} from '@opallabs/api'
import { services } from '@opallabs/api/v2'
import type { AssetReference } from '@opallabs/cassini'
import { testFeature } from '@opallabs/principles'
import { queryKeys } from './queryKeys'

type Service = OpalApiComponent<'V2', 'service'>
type Account = OpalApiComponent<'V2', 'account'>
type ServiceDocument = OpalApiResponseDocument<'ReadServicesV2', 200>

export type ServiceOption = {
  id: string
  iconName?: string | null
  name: string
  systemName: string
  assetReference: AssetReference | null
}

type NumericId = string
type ServiceIcon = {
  id: string
  iconName?: string
  name: string
  systemName: string
  color?: string
  assetSrc?: string
}
type ServiceIconMap = Record<NumericId, ServiceIcon>

export const useSystemServices = <T = ServiceDocument>({
  brandFilter,
  include,
  select,
}: {
  brandFilter: (string | number)[]
  include?: ('accounts' | 'post_types' | 'asset_reference')[]
  select?: (data: ServiceDocument) => T
}): UseQueryResult<T, unknown> => {
  return useQuery({
    queryKey: queryKeys.services({
      include,
      filter: { brand_id: brandFilter },
    }),
    queryFn: () =>
      services.read({
        params: {
          query: {
            include,
            filter: { brand_id: brandFilter },
            page: { limit: 500, offset: 0 },
          },
        },
      }),
    select,
  })
}

type FilterByAccountAndBrand = (arg: {
  serviceDocument: ServiceDocument
  service: Service
}) => boolean

const filterByAccountAndBrand: FilterByAccountAndBrand = ({
  serviceDocument,
  service,
}) => {
  if (!service.relationships.accounts.data.length) return false
  const accountIds = service.relationships.accounts.data.map(
    (account) => account.id
  )
  const accountsForService = serviceDocument.included?.filter(
    (include) => include.type === 'account' && accountIds.includes(include.id)
  ) as Account[]

  return Boolean(accountsForService.length)
}

const includeAssetReference = (
  relationship: { id: string } | null,
  includes?: ServiceDocument['included']
) => {
  if (!relationship || !includes || !includes.length) return null

  const assetReference = includes.find(
    (include) =>
      include.type === 'asset_reference' && include.id === relationship.id
  )

  if (!assetReference) return null
  return assetReference.attributes as AssetReference
}

// Flag-gated channels stay out of the picker until the flag is on; the icon
// and account maps still include them so existing content renders.
const isPickable = (service: Service) =>
  service.attributes.system_name !== 'sharepoint' ||
  testFeature('sharepoint_integration')

const selectServices = (serviceDocument: ServiceDocument): ServiceOption[] =>
  serviceDocument.data
    .filter(isPickable)
    .filter((service) => filterByAccountAndBrand({ serviceDocument, service }))
    .map<ServiceOption>((service) => ({
      id: service.id,
      iconName: service.attributes.icon_name,
      name: service.attributes.name,
      systemName: service.attributes.system_name,
      assetReference: includeAssetReference(
        service.relationships.asset_reference.data,
        serviceDocument.included
      ),
    }))
    .sort((a, b) => {
      if (a.name > b.name) return 1
      if (a.name < b.name) return -1
      return 0
    })

const selectServiceIcons = (
  serviceDocument: ServiceDocument
): ServiceIconMap => {
  const assets = serviceDocument.included?.filter(
    isResourceOfType('asset_reference', 'V2')
  )

  return serviceDocument.data.reduce<ServiceIconMap>(
    (serviceIconMap, service) => {
      const serviceAsset = assets?.find(
        (asset) => asset.id === service.relationships.asset_reference.data?.id
      )

      serviceIconMap[service.id] = {
        id: service.id,
        iconName: service.attributes.icon_name ?? undefined,
        name: service.attributes.name,
        systemName: service.attributes.system_name,
        color: service.attributes.color,
        assetSrc: serviceAsset?.attributes.preview_url ?? undefined,
      }

      return serviceIconMap
    },
    {}
  )
}

export const useWorkspaceServiceIconMap = (
  brandId: string
): UseQueryResult<ServiceIconMap> =>
  useSystemServices({
    select: selectServiceIcons,
    include: ['asset_reference', 'accounts'],
    brandFilter: ['null', brandId],
  })

export const useSystemServiceOptions = (
  brandId: string
): UseQueryResult<Array<ServiceOption>, unknown> =>
  useSystemServices<Array<ServiceOption>>({
    select: selectServices,
    include: ['asset_reference', 'accounts'],
    brandFilter: ['null', brandId],
  })

// Map of account-name → serviceId for the workspace. Content mode's
// creator uses this to pre-select a channel when the sort dimension is
// Account: the sort categorizes each cell by an account name, and the
// channel for that account is deterministic (an account belongs to one
// service). Keyed by name rather than id because the content category
// getter surfaces the account's name, not id.
const selectAccountToServiceMap = (
  serviceDocument: ServiceDocument
): Record<string, string> => {
  const map: Record<string, string> = {}
  const accounts = (serviceDocument.included ?? []).filter(
    (included): included is Account => included.type === 'account'
  )
  serviceDocument.data.forEach((service) => {
    service.relationships.accounts.data.forEach((accountRef) => {
      const account = accounts.find((a) => a.id === accountRef.id)
      const name = account?.attributes?.name
      if (name && !map[name]) {
        map[name] = service.id
      }
    })
  })
  return map
}

export const useAccountToServiceMap = (
  brandId: string
): UseQueryResult<Record<string, string>, unknown> =>
  useSystemServices<Record<string, string>>({
    select: selectAccountToServiceMap,
    // Match the include shape used by `useSystemServiceOptions` /
    // `useWorkspaceServiceIconMap` so all three hooks share a single
    // React Query cache entry and the workspace fetches services once
    // per mount instead of twice.
    include: ['asset_reference', 'accounts'],
    brandFilter: ['null', brandId],
  })
